import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Smartphone, CheckCircle, Loader2, Copy } from 'lucide-react';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [paymentId, setPaymentId] = useState(null);
  const [stage, setStage] = useState('form'); // 'form' | 'pix'
  const [pixData, setPixData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    cpf: ''
  });
  const [errors, setErrors] = useState({});
  const pollingIntervalRef = useRef(null);

  // Polling for PIX status
  const startPaymentPolling = (pId) => {
    console.log('🔄 Iniciando polling Asaas...');
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${pId}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'approved') {
            clearInterval(pollingIntervalRef.current);
            navigate(`/checkout/success?payment_id=${pId}`);
          }
        }
      } catch (error) {
        console.error('❌ Erro ao verificar status:', error);
      }
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email || !formData.email.includes('@')) newErrors.email = 'Email inválido';
    if (!formData.full_name || formData.full_name.length < 3) newErrors.full_name = 'Nome completo é obrigatório';
    if (!formData.phone || formData.phone.replace(/\D/g, '').length < 10) newErrors.phone = 'Telefone inválido';
    if (!formData.cpf || formData.cpf.replace(/\D/g, '').length !== 11) newErrors.cpf = 'CPF inválido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  const formatCPF = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  // Step 1: Create payment record and proceed to payment selection
  const handleContinue = async () => {
    if (!validateForm()) {
      alert('Por favor, preencha todos os campos corretamente');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          payment_method: 'pix',
          plan_type: selectedPlan
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Erro ao criar checkout');

      setPaymentId(data.payment_id);
      await generatePix(data.payment_id);
    } catch (error) {
      console.error('❌ Erro:', error);
      alert(`Erro: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const generatePix = async (pId) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/create-pix-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_id: pId,
          payer: {
            email: formData.email,
            first_name: formData.full_name.split(' ')[0],
            last_name: formData.full_name.split(' ').slice(1).join(' '),
            identification: {
              type: 'CPF',
              number: formData.cpf.replace(/\D/g, '')
            }
          }
        })
      });

      const data = await response.json();
      if (!response.ok || !data.qr_code) throw new Error(data.detail || 'Erro ao gerar PIX');

      setPixData({
        qr_code: data.qr_code,
        qr_code_base64: data.qr_code_base64,
        ticket_url: data.ticket_url
      });
      setStage('pix');
      startPaymentPolling(pId);
    } catch (error) {
      console.error('❌ Erro PIX:', error);
      alert(`Erro ao gerar PIX: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyPix = () => {
    navigator.clipboard.writeText(pixData.qr_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const goBackToForm = () => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    setStage('form');
    setPaymentId(null);
    setPixData(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="bg-slate-900/50 border-b border-slate-800">
        <div className="container mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/assinar')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="checkout-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Voltar</span>
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Column - Form / Payment */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 md:p-8">
              <h1 className="text-2xl md:text-3xl font-black text-white mb-2">Finalizar Assinatura</h1>
              <p className="text-gray-400 mb-4">Preencha seus dados para continuar</p>

              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="bg-green-500/20 p-2 rounded-lg flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-green-400 font-semibold mb-1">📱 Entrega Automática via WhatsApp</h3>
                    <p className="text-gray-300 text-sm">
                      Após a confirmação do pagamento, suas credenciais serão enviadas <strong>automaticamente</strong> via WhatsApp.
                    </p>
                  </div>
                </div>
              </div>

              {stage === 'form' && (
                <>
                  {/* Plan Selection */}
                  <div className="mb-6">
                    <h3 className="text-white font-semibold mb-3">📦 Escolha seu plano:</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div
                        onClick={() => setSelectedPlan('monthly')}
                        data-testid="plan-monthly-btn"
                        className={`relative cursor-pointer p-4 rounded-xl border-2 transition-all ${selectedPlan === 'monthly' ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="text-white font-bold text-lg">Mensal</h4>
                            <p className="text-gray-400 text-xs">Renovação a cada 30 dias</p>
                          </div>
                          {selectedPlan === 'monthly' && <CheckCircle className="w-5 h-5 text-blue-500" />}
                        </div>
                        <div className="mt-3">
                          <p className="text-3xl font-black text-white">R$ 29,90</p>
                          <p className="text-gray-400 text-sm">por mês</p>
                        </div>
                      </div>

                      <div
                        onClick={() => setSelectedPlan('quarterly')}
                        data-testid="plan-quarterly-btn"
                        className={`relative cursor-pointer p-4 rounded-xl border-2 transition-all ${selectedPlan === 'quarterly' ? 'border-green-500 bg-green-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'}`}
                      >
                        <div className="absolute -top-2 -right-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">11% OFF</div>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="text-white font-bold text-lg">Trimestral</h4>
                            <p className="text-gray-400 text-xs">Pague de uma vez</p>
                          </div>
                          {selectedPlan === 'quarterly' && <CheckCircle className="w-5 h-5 text-green-500" />}
                        </div>
                        <div className="mt-3">
                          <p className="text-3xl font-black text-white">R$ 79,90</p>
                          <p className="text-gray-400 text-sm line-through">R$ 89,70</p>
                          <p className="text-green-400 text-xs font-semibold mt-1">💰 Economize R$ 9,80!</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={(e) => { e.preventDefault(); handleContinue(); }} className="space-y-5">
                    <div>
                      <label className="block text-white font-medium mb-2">Nome Completo *</label>
                      <input
                        type="text" name="full_name" value={formData.full_name}
                        onChange={handleChange} placeholder="João Silva"
                        data-testid="checkout-name-input"
                        className={`w-full px-4 py-3 bg-slate-800 border ${errors.full_name ? 'border-red-500' : 'border-slate-700'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors`}
                        required
                      />
                      {errors.full_name && <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>}
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">Email *</label>
                      <input
                        type="email" name="email" value={formData.email}
                        onChange={handleChange} placeholder="joao@exemplo.com"
                        data-testid="checkout-email-input"
                        className={`w-full px-4 py-3 bg-slate-800 border ${errors.email ? 'border-red-500' : 'border-slate-700'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors`}
                        required
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">📱 WhatsApp (com DDD) *</label>
                      <input
                        type="tel" name="phone" value={formData.phone}
                        onChange={(e) => handleChange({ target: { name: 'phone', value: formatPhone(e.target.value) } })}
                        placeholder="(83) 99999-9999"
                        data-testid="checkout-phone-input"
                        className={`w-full px-4 py-3 bg-slate-800 border ${errors.phone ? 'border-red-500' : 'border-slate-700'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors`}
                        required
                      />
                      {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                      <p className="text-green-400 text-sm mt-2 font-medium">✅ Suas credenciais serão enviadas neste WhatsApp após aprovação</p>
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">CPF *</label>
                      <input
                        type="text" name="cpf" value={formData.cpf}
                        onChange={(e) => handleChange({ target: { name: 'cpf', value: formatCPF(e.target.value) } })}
                        placeholder="000.000.000-00"
                        data-testid="checkout-cpf-input"
                        className={`w-full px-4 py-3 bg-slate-800 border ${errors.cpf ? 'border-red-500' : 'border-slate-700'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors`}
                        required
                      />
                      {errors.cpf && <p className="text-red-500 text-sm mt-1">{errors.cpf}</p>}
                    </div>

                    <button
                      type="submit" disabled={loading}
                      data-testid="checkout-submit-btn"
                      className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /><span>Gerando PIX...</span></>
                      ) : (
                        <><Smartphone className="w-5 h-5" /><span>Gerar PIX e Continuar</span></>
                      )}
                    </button>

                    <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                      <Lock className="w-4 h-4" />
                      <span>Pagamento 100% seguro via Asaas</span>
                    </div>
                  </form>
                </>
              )}

              {stage === 'pix' && pixData && (
                <div data-testid="pix-payment-section">
                  <div className="mb-4 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
                    <p className="text-cyan-400 text-sm font-medium">📱 Escaneie o QR Code ou copie o código PIX abaixo</p>
                  </div>

                  <div className="bg-white p-6 rounded-xl text-center mb-4">
                    {pixData.qr_code_base64 ? (
                      <img src={`data:image/png;base64,${pixData.qr_code_base64}`} alt="QR Code PIX" className="w-64 h-64 mx-auto" data-testid="pix-qrcode-img" />
                    ) : (
                      <div className="text-gray-700">QR Code não disponível</div>
                    )}
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-400 mb-2">Ou copie o código PIX:</p>
                    <div className="flex gap-2">
                      <input
                        type="text" value={pixData.qr_code} readOnly
                        data-testid="pix-code-input"
                        className="flex-1 px-3 py-2 bg-slate-800 text-white border border-slate-700 rounded-lg text-xs font-mono"
                      />
                      <button
                        onClick={copyPix}
                        data-testid="pix-copy-btn"
                        className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        {copied ? 'Copiado!' : 'Copiar'}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-yellow-400" />
                    <p className="text-sm text-yellow-400">⏱️ Aguardando pagamento... Você será redirecionado automaticamente após a confirmação.</p>
                  </div>

                  <button
                    onClick={goBackToForm}
                    data-testid="checkout-back-form-btn"
                    className="w-full mt-4 py-3 bg-slate-700 text-white font-medium rounded-xl hover:bg-slate-600 transition-colors"
                  >
                    ← Voltar para editar dados
                  </button>
                </div>
              )}
            </div>

            {/* Right Column - Summary */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-bold text-white mb-4">Resumo da Assinatura</h2>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">
                      {selectedPlan === 'quarterly' ? 'Plano Trimestral (3 meses)' : 'Plano Mensal'}
                    </span>
                    <span className="text-white font-semibold">
                      {selectedPlan === 'quarterly' ? 'R$ 79,90' : 'R$ 29,90'}
                    </span>
                  </div>
                  {selectedPlan === 'quarterly' && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Valor sem desconto</span>
                      <span className="text-gray-500 line-through">R$ 89,70</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-4 border-t border-slate-700">
                    <span className="text-white font-bold text-lg">Total</span>
                    <span className="text-cyan-400 font-black text-2xl">
                      {selectedPlan === 'quarterly' ? 'R$ 79,90' : 'R$ 29,90'}
                      {selectedPlan === 'monthly' && '/mês'}
                    </span>
                  </div>
                </div>

                {selectedPlan === 'quarterly' ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
                    <p className="text-green-400 text-sm font-medium">🔥 Economize R$ 9,80 no plano trimestral (11% de desconto)!</p>
                  </div>
                ) : (
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 mb-6">
                    <p className="text-cyan-400 text-sm font-medium">💎 40% de desconto aplicado! De R$ 49,90 por apenas R$ 29,90</p>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">Acesso imediato após aprovação</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">Credenciais enviadas via WhatsApp</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">Cancele quando quiser</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-6 h-6 text-green-500" />
                  <h3 className="font-bold text-white">Pagamento Seguro</h3>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Processamos pagamentos através do <strong className="text-cyan-400">Asaas</strong> com criptografia SSL e certificação PCI DSS.
                  Sua chave PIX é processada com segurança pelo Asaas — nenhum dado sensível é armazenado em nossos servidores.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
